import { create } from 'zustand';
import type { Fleet, FleetsView } from '@kaeliux/shared';
import { getFleets } from '../services/fleet.service';
import { usePlanetStore } from './planetStore';
import { usePlayerStore } from './playerStore';
import { queryClient } from '../queryClient';

interface FleetStoreState {
    /**
     * Null until the first load — the Fleets page renders its flight-limit
     * counters with defaults in that window, so "not loaded yet" and "loaded,
     * no fleets" have to stay distinguishable.
     */
    view: FleetsView | null;
    error: string | null;
    /**
     * Bumped on every successful load and on `applyView`. Every `getFleets`
     * call processes due arrivals/returns first, so any of them may have
     * written a new log entry (arrival logs in particular, for missions still
     * `returning` and hence still counted as active) — the Fleets page keys
     * its log sections off this to reset them to page 1.
     */
    refreshSeq: number;
    loadFleets: () => Promise<void>;
    /**
     * Adopt a view returned by a mutation (`sendFleet`) without a refetch. A
     * colonize flight in it is recorded on the player state at once, so the
     * tutorial's Colonize step counts it even if the flight later turns back.
     */
    applyView: (view: FleetsView) => void;
}

// Module scope rather than store fields: these are bookkeeping for the fetch
// itself and must never trigger a re-render.
let inFlight: Promise<void> | null = null;
let activeFleetCount: number | null = null;
let fleetSignature: string | null = null;
let knownSunAttackIds = new Set<string>();
let fleetEpoch = 0;

/**
 * A fleet dropping out of the active list means it landed (or was consumed at
 * its target) — its ships/cargo just docked, so refresh the planet snapshot
 * immediately rather than leaving ship rows stale until the next heartbeat.
 * A resolved fleet may also have founded a colony, so the planet list has to
 * be refreshed too or the switcher won't show it without a reload.
 *
 * The owner id comes off the loaded planet rather than `authStore`: fleets are
 * player-scoped and the planet already carries it, so this store needs no edge
 * to the auth chain.
 */
function onActiveFleetCountChanged(fleets: Fleet[]) {
    const nextCount = fleets.length;
    const userId = usePlanetStore.getState().currentPlanet?.userId;
    if (activeFleetCount !== null && nextCount < activeFleetCount && userId) {
        usePlanetStore.getState().loadPlanet(userId).catch(() => { });
        usePlanetStore.getState().loadPlanets(userId).catch(() => { });
    }
    refreshFleetDependentQueries(fleets);
    activeFleetCount = nextCount;
}

/**
 * A fleet sent to, recalled from, or landed at the Team planet changes its
 * ships and its incoming population — refresh the Team planet queries now
 * rather than on their next 15s poll. Keyed on every fleet's id and status, so
 * a recall (same count) or a send and a landing in one refresh still register.
 *
 * The alliance query is refreshed only when a Sun Attack newly appears: that
 * dispatch stamps the alliance, which the tutorial reads. It has a permanent
 * observer on every page, so refetching it on every unrelated fleet change —
 * or while a long-running Sun Attack merely stays in flight — would be a
 * costly request for nothing.
 */
function refreshFleetDependentQueries(fleets: Fleet[]) {
    const signature = fleets.map((f) => `${f.id}:${f.status}`).sort().join('|');
    const sunAttackIds = new Set(fleets.filter((f) => f.mission === 'sunAttack').map((f) => f.id));
    if (fleetSignature !== null && signature !== fleetSignature) {
        queryClient.invalidateQueries({ queryKey: ['teamPlanet'] }).catch(() => { });
        if ([...sunAttackIds].some((id) => !knownSunAttackIds.has(id))) {
            queryClient.invalidateQueries({ queryKey: ['alliance', 'mine'] }).catch(() => { });
        }
    }
    fleetSignature = signature;
    knownSunAttackIds = sunAttackIds;
}

export const useFleetStore = create<FleetStoreState>((set, get) => ({
    view: null,
    error: null,
    refreshSeq: 0,

    loadFleets: async () => {
        // Coalesce rather than drop: callers that `await` this (recall, the
        // game loop) must observe the fresh view, and returning early would
        // hand them a resolved promise over stale state whenever a periodic
        // refresh happened to be in flight.
        if (inFlight) return inFlight;

        const epoch = ++fleetEpoch;
        inFlight = (async () => {
            try {
                const next = await getFleets();
                // A `sendFleet` response adopted while this was in flight is
                // strictly newer — don't clobber it with our older list.
                if (epoch !== fleetEpoch) return;
                onActiveFleetCountChanged(next.fleets);
                set({ view: next, error: null, refreshSeq: get().refreshSeq + 1 });
            } catch (err) {
                if (epoch !== fleetEpoch) return;
                set({ error: err instanceof Error ? err.message : 'Failed to load fleets' });
            } finally {
                inFlight = null;
            }
        })();

        return inFlight;
    },

    applyView: (view: FleetsView) => {
        fleetEpoch++;
        // Reset the baseline here too, so a fleet dispatched now is still
        // detected as resolved when it later drops off the active list.
        refreshFleetDependentQueries(view.fleets);
        activeFleetCount = view.fleets.length;
        if (view.fleets.some((f) => f.mission === 'colonize')) usePlayerStore.setState({ colonizeFlightSent: true });
        set({ view, error: null, refreshSeq: get().refreshSeq + 1 });
    },
}));

/** Test-only: clear the module-scope fetch bookkeeping between cases. */
export function __resetFleetStoreForTests() {
    inFlight = null;
    activeFleetCount = null;
    fleetSignature = null;
    knownSunAttackIds = new Set();
    fleetEpoch = 0;
    useFleetStore.setState({ view: null, error: null, refreshSeq: 0 });
}
